import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';

import { getSisStudentById, searchSisStudents } from '../../../api/services/SisImport';

import { SisMeta, SisPagination, SisRecord, SisStudentDetails } from './types';

const buildPagination = (meta: SisMeta): SisPagination => ({
  page: meta.page,
  perPage: meta.perPage,
  totalCount: meta.totalCount,
  totalPages: Math.ceil(meta.totalCount / meta.perPage) || 1,
});

interface UseSisStudentSearch {
  records: SisRecord[];
  isLoading: boolean;
  pagination: SisPagination | null;
  search: (query: string) => void;
  loadMore: () => void;
  clearResults: () => void;
  getStudentById: (id: number) => Promise<SisStudentDetails | null>;
  isImporting: boolean;
  hasNoRecords: boolean;
}

export const useSisStudentSearch = (initialHasNoRecords: boolean = false): UseSisStudentSearch => {
  const [records, setRecords] = useState<SisRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [pagination, setPagination] = useState<SisPagination | null>(null);
  const [hasNoRecords] = useState<boolean>(initialHasNoRecords);
  const currentQueryRef = useRef<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const fetchRecords = useCallback(async (query: string, page: number = 1) => {
    if (!query.trim()) {
      setRecords([]);
      setPagination(null);

      return;
    }

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    currentQueryRef.current = query;
    setIsLoading(true);

    try {
      const response = await searchSisStudents(query, page, abortControllerRef.current.signal);

      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      if (response.type === 'success') {
        const { students, meta } = response.data;
        const nextPagination = buildPagination(meta);

        if (page === 1) {
          setRecords(students);
        } else {
          setRecords((prev) => [...prev, ...students]);
        }
        setPagination(nextPagination);
      } else {
        setRecords([]);
        setPagination(null);
      }
    } catch {
      // Handle error silently - request was likely aborted
      setRecords([]);
      setPagination(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debouncedSearch = useMemo(() => debounce((query: string) => fetchRecords(query, 1), 400), [fetchRecords]);

  const search = useCallback(
    (query: string) => {
      debouncedSearch(query);
    },
    [debouncedSearch]
  );

  const loadMore = useCallback(() => {
    if (pagination && pagination.page < pagination.totalPages && !isLoading) {
      fetchRecords(currentQueryRef.current, pagination.page + 1);
    }
  }, [pagination, isLoading, fetchRecords]);

  const clearResults = useCallback(() => {
    setRecords([]);
    setPagination(null);
    currentQueryRef.current = '';
  }, []);

  const getStudentById = useCallback(async (id: number): Promise<SisStudentDetails | null> => {
    setIsImporting(true);
    try {
      const response = await getSisStudentById(id);

      if (response.type === 'success') {
        return response.data.student;
      }

      return null;
    } catch {
      return null;
    } finally {
      setIsImporting(false);
    }
  }, []);

  return {
    records,
    isLoading,
    pagination,
    search,
    loadMore,
    clearResults,
    getStudentById,
    isImporting,
    hasNoRecords,
  };
};
